// src/data/penaltyClaim.js
//
// RULING 257 (proposed 2026-09-29, preview only - NOT LIVE until Bob approves the preview).
// THE PHYSICAL PENALTY TIME CLAIM. Replaces the business-letter design Bob rejected ("horrible and nothing
// like I envisioned"). Modeled on Bob's own blank form (Blank Generic CSX Claim Grievance Form, page 8,
// "Penalty Time Claim"), with his changes of 2026-09-29:
//   1. "Tour/" deleted - the field is "Time of Violation".
//   2. Time and amount coordinated: hours per date x dates = total hours (and dollars only when the rate
//      is on file). A mismatch never prints - it goes to `missing`.
//   3. Standing sentence: "Submitted in accordance with the rules under the controlling agreement."
//   4. Substance of Claim is culled only from the claim's own entries. The platform adds no argument
//      (ruling 27); it only labels and arranges what the claimant entered.
//   5. Closing paragraph corrected (approved by Bob, "ok").
//   6. "Payment ... is warranted" followed by the reason, with Third Division Awards 29232 and 29913
//      (both approved by Bob). Quotes verified against the award PDFs 2026-09-29.
//   7. Signed copies are always saved with the case (enforced by the panel / status gate, not here).
//   8. As many pages as a proper claim needs; one page is the target, never at the cost of legibility.
// Rulings kept: 188 (parent International heads it), 236 (committee letterhead when set), 110 (platform
// only in the footer), 237/238 (unsigned - the signature is the signer's own act), 256 (claimant may
// view, approve, print and serve it herself).
//
// Pure module: no React, no Firestore, no imports. Everything missing goes to `missing`; nothing
// missing is ever printed as a note on the claim.

import { withVersionTag } from './claimVersion';   // Record 651 (ruling 305.11)
import { guidedQuestions } from './box1Guided';    // Bob 2026-10-17: the overtime job check (box1Guided imports nothing)
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function nonEmpty(v) { return v !== null && v !== undefined && String(v).trim() !== ''; }
function s(v) { return nonEmpty(v) ? String(v).trim() : ''; }

export function longDate(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s(v));
  if (!m) return s(v);
  const mo = parseInt(m[2], 10), d = parseInt(m[3], 10);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return s(v);
  return MONTHS[mo - 1] + ' ' + d + ', ' + m[1];
}
export function money(n) {
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function num(v) {
  const n = typeof v === 'number' ? v : parseFloat(String(v == null ? '' : v).replace(/[$,\s]/g, ''));
  return isFinite(n) && n > 0 ? n : 0;
}
function fmtHours(h) { return (Math.round(h * 100) / 100).toString(); }

export const STANDING_SENTENCE = 'Submitted in accordance with the rules under the controlling agreement.';

// Repetition removed (Bob, 2026-09-29): "a proper device to police the Agreement" appeared twice (here and in the
// Award 29232 quote), and the full-employment point twice (here and in Award 29913). Each point now appears once:
// the closing states the Organization's position; the payment paragraph gives the reason and the awards.
export const CLOSING_PARAGRAPH =
  'The Claimant is due compensation even though the Claimant may have worked and been paid on some or all ' +
  'of the claim dates. The Agreement cannot be violated with impunity, and the Carrier must accept the ' +
  'consequences of its violation.';

// Verified 2026-09-29 against the award PDFs on static.smart-union.org (Yardmaster awards menu 70), and again
// 2026-09-30 (section 537) with the surrounding text read:
//   3-29232 (Docket MW-28668, Referee William E. Fredenberger, Jr., May 18, 1992; BMWE v. Elgin, Joliet and Eastern):
//     "We agree with the Organization that Claimants are due compensation despite the fact they worked and received
//     compensation on the Claim dates. Claimants in fact did lose work opportunities due to the Carrier's violation of
//     the Agreement, and this type of Claim long has been viewed as a proper device to police the Agreement."
//   3-29913 (Docket MW-29652, Referee Elizabeth C. Wesman, November 9, 1993; BMWE v. CSX): the "full employment" line is
//     Award 27614's, adopted by 29913 - cited "(quoting Third Division Award 27614)" so the carrier cannot say it was
//     misattributed.
// Bob, 2026-09-30: "regarding the awards - we need a short, 1 to 2 sentences to explain how or why this rule is
// applicable, and warranted". Each award now carries one sentence saying what the Board held and why it fits.
// Section 543: the document review found the first sentence here at 52 words; split in two, the Board's words unchanged.
export const PAYMENT_PARAGRAPH =
  'Payment for this claim as submitted is warranted because the Claimant lost a work opportunity due to the ' +
  'Carrier\u2019s violation of the Agreement. In Third Division Award 29232 the Board held that claimants who worked ' +
  'and were paid on the claim dates were still due compensation for the work opportunity they lost. The Board held that ' +
  '\u201Cthis type of Claim long has been viewed as a proper device to police the Agreement.\u201D In Third Division Award 29913 the ' +
  'Board rejected the Carrier\u2019s defense that the Claimant was fully employed: \u201C[F]ull employment of the Claimants ' +
  'is not a valid defense in a dispute such as involved here\u201D (quoting Third Division Award 27614).';

// Bob, 2026-09-30: "If there are award numbers regarding 'The Claimant is due compensation even though ...', then they
// should be listed or provided and can be used as an exhibit." Every award below was READ (section 537); each holds
// that a claimant who worked and was paid is still due compensation for the violation. The closing names them and the
// attachments list copies of them. Counter-authority the Carrier may raise (Awards 18305, 18687, the "actual loss"
// line) is recorded for the officer in SUPPORTING_AWARDS_NOTE and shown on the claim page, never printed on the claim.
export const SUPPORTING_AWARDS = [
  { no: '29232', held: 'Claimants are due compensation despite having worked and been paid on the claim dates; the claim polices the Agreement.' },
  { no: '29913', held: 'Full employment is no defense (quoting Award 27614).' },
  { no: '27614', held: 'Full employment of the claimants is not a valid defense.' },
  { no: '26593', held: 'The Agreement is enforced only if damages are awarded even where there is no actual loss.' },
  { no: '21678', held: 'Claimants who lost their rightful opportunity to perform the work are entitled to a monetary claim.' },
  { no: '19899', held: 'Damages may be sustained even though the employees were fully employed at all relevant times.' },
];
export const SUPPORTING_AWARDS_URL = (no) => 'https://static.smart-union.org/worksite/yardmaster/awards/PDF-AWDS/Ymst-3rd-div/3-' + no + '.PDF';
export const SUPPORTING_AWARDS_NOTE = 'The Carrier may cite the other line of awards - that damages are limited to actual monetary loss (Third ' +
  'Division Awards 18305 and 18687, recorded in Award 19899). Award 19899 answers them. Award 33994 bars double payment for the same days.';
// RULING 279 (Bob, 2026-10-04: "YES"): the closing list names every award the form relies on. 29913 and 27614 are cited in
// PAYMENT_PARAGRAPH and attached (AWARDS_ATTACHMENT) but were missing here; the claim page's document review flagged it
// on claim 0013. Same order as the attachment.
export const CLOSING_AWARDS = 'See Third Division Awards 29232, 29913, 27614, 26593, 21678 and 19899.';
export const AWARDS_ATTACHMENT = 'Copies of National Railroad Adjustment Board, Third Division Awards ' +
  SUPPORTING_AWARDS.map(a => a.no).slice(0, -1).join(', ') + ' and ' + SUPPORTING_AWARDS[SUPPORTING_AWARDS.length - 1].no;

// Section 537: the receipt line at the foot of the claim follows the property's service terms (Bob: "on some properties
// they have a different agreement regarding service between the carrier and the organization"). The agreement's
// requirements carry `service`; with none, the hand receipt prints as before.
export const DEFAULT_RECEIPT = { left: 'Recipient\u2019s Signature & Title', right: 'Date & Time Received' };

// Exhibit labels are written to the claimant on screen ("Your time record..."). On the claim they speak of
// the Claimant. Only the pronoun and the date format change; nothing is added.
function attachmentLabel(desc, claimant) {
  let t = s(desc);
  const dup = / - Rule .*$/.exec(t);                       // "Text of Rule X - Rule X (Agreement)" -> "Text of Rule X"
  if (/^Text of /.test(t) && dup) t = t.slice(0, dup.index);
  t = t.replace(/^Your /, 'Claimant’s ').replace(/\byou and\b/g, 'Claimant and');
  t = t.replace(/(\d{4}-\d{2}-\d{2})/g, (m) => longDate(m));
  return t;
}

// Claim dates after the first, with consecutive days collapsed: "September 2\u20134, 2026; September 9, 2026".
export function dateRanges(isoList) {
  const ds = Array.from(new Set((isoList || []).filter(v => /^\d{4}-\d{2}-\d{2}$/.test(s(v))))).sort();
  const out = []; let i = 0;
  while (i < ds.length) {
    let j = i;
    while (j + 1 < ds.length && (Date.parse(ds[j + 1]) - Date.parse(ds[j])) === 86400000) j++;
    if (j === i) out.push(longDate(ds[i]));
    else {
      const a = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ds[i]), b = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ds[j]);
      out.push(a[1] === b[1] && a[2] === b[2] ? MONTHS[+a[2] - 1] + ' ' + (+a[3]) + '\u2013' + (+b[3]) + ', ' + a[1] : longDate(ds[i]) + ' \u2013 ' + longDate(ds[j]));
    }
    i = j + 1;
  }
  return out.join('; ');
}

// STATEMENT OF CLAIM (Bob, 2026-09-29): "rather than use the exact words taken directly from the claimant's
// description of the event, when cross referenced with the agreement rules, we can use the language of the
// agreement to better state what happened." PROPOSED RULING 258 - amends ruling 27 for this one section:
//   - The platform DRAFTS the statement from the claim's own fields plus the relied-on rule's own words, using a
//     fixed template per violation type (below). No free-written argument; nothing the claim does not hold.
//   - The draft never goes out on its own authority: the claimant (or the officer filing for her) reads it
//     beside her own words and approves or edits it. Until approved, the claim is NOT ready to send.
//   - Her original description stays on the claim record, unchanged.
const junior = (c) => nonEmpty(c.violatorName)
  ? s(c.violatorName) + (nonEmpty(c.violatorRosterNumber) ? ' (roster no. ' + s(c.violatorRosterNumber) + ')' : '')
  : s((c.box1Answers || {}).q_junior);
// The kind of claim the member chose (ruling 261) -> the template used when the rule carries no id of its own.
export const KIND_TEMPLATE = { vacation: 'VAC-seniority-order', position: 'POS-award-of-position' };
const TEMPLATES = {
  // Conrail M/S Rule 13(D) and the like: vacation schedule arranged out of seniority order.
  'VAC-seniority-order': (c, r, f) =>
    'On ' + f.date + (f.place ? ', at ' + f.place : '') + ', the Carrier assigned vacation' +
    (f.answers.q_vac_weeks ? ' for ' + f.answers.q_vac_weeks : '') + ' to ' + junior(c) +
    ', an employee junior to the Claimant, ahead of the Claimant' + (f.seniority ? ' (seniority date ' + f.seniority + ')' : '') + '. ' +
    ruleName(r) + ' provides that \u201C' + f.quote + '\u201D The Carrier did not give the Claimant\u2019s preference due ' +
    'regard in seniority order.',
  // Award of a bulletined position to a junior bidder.
  'POS-award-of-position': (c, r, f) =>
    'On ' + f.date + (f.place ? ', at ' + f.place : '') + ', the Carrier awarded the position to ' + junior(c) +
    ', an employee junior to the Claimant, over the Claimant' + (f.seniority ? ' (seniority date ' + f.seniority + ')' : '') + '. ' +
    ruleName(r) + ' provides that \u201C' + f.quote + '\u201D The Claimant was the senior qualified bidder.',
};
// SECTION 521 (2026-09-30): some catalogues label a rule "RULE/ARTICLE — RULE NO. 7 — VACATIONS"; the form printed the
// catalogue's scaffolding. The leading "RULE/ARTICLE —" is dropped on the form; the rest is the catalogue's own words.
export function ruleName(r) { return s(r && (r.rule || r.ruleId)).replace(/^RULE\/ARTICLE\s*[\u2014\u2013-]\s*/i, ''); }
function lowerFirst(q) { return q ? q.charAt(0).toLowerCase() + q.slice(1) : q; }

// buildPenaltyClaim(claim, { header, today })
//   header - letterhead lines (236) or [parent International, committee]; supplied by the caller.
export function buildPenaltyClaim(claim, opts) {
  const o = opts || {};
  const c = claim || {};
  const missing = [];
  const check = [];                                         // printed fine, but the filer must verify
  const need = (label, v) => { if (!nonEmpty(v)) missing.push(label); return s(v); };

  const number = need('the claim number', c.id);
  // Record 607: a name typed all lower case ("bob keeley") prints capitalised; any other casing is left as typed.
  const claimant = (n => (n && n === n.toLowerCase()) ? n.replace(/(^|[\s'-])([a-z])/g, (m, p1, ch) => p1 + ch.toUpperCase()) : n)(need("the claimant's name", c.claimantName));
  const employeeNo = need("the claimant's employee number", c.employeeNumber);
  // Record 635: the CLAIMANT's seniority date. subjectSeniorityDate is the other employee's (entered beside violatorName,
  // NewClaim.js / QuickFile.js) and printed here as hers until 2026-10-11 - never again.
  const seniority = need("the claimant's seniority date", c.seniorityDate);
  const occurred = need('the date of violation', c.incidentDate);
  const time = need('the time of violation', c.incidentTime24 || c.incidentTime);
  const agreement = need('the controlling agreement', c.applicableAgreement);

  // Dates of violation: the claim's own list when it has one, else the single date of violation.
  const dates = (Array.isArray(c.claimDates) && c.claimDates.length ? c.claimDates : [c.incidentDate]).filter(nonEmpty);

  // Articles violated - the rules the claimant relies on, by name, plus the agreement.
  // Section 528 (2026-09-30, claim 0013 on the call): the form printed ARTICLE 21 four times and ARTICLE 11 twice - the
  // claim held several entries naming the same rule. Each rule is NAMED once in Article(s) Violated; each distinct quotation prints once in the substance.
  const seenRule = new Set();
  const rules = (Array.isArray(c.matchedRules) ? c.matchedRules : []).filter(r => r && !r.setAsideAt).filter(r => {
    // Same rule AND same quotation = a true duplicate. Two different paragraphs of one article both stay in the substance.
    const k = ruleName(r).toUpperCase().replace(/\s+/g, ' ').trim() + '|' + s(r.exactLanguage).replace(/\s+/g, ' ').trim();
    if (!ruleName(r) || seenRule.has(k)) return false;
    seenRule.add(k); return true;
  });
  if (!rules.length) missing.push('the article(s) violated');
  const articles = Array.from(new Set(rules.map(r => ruleName(r)).filter(nonEmpty))).join('; ') + (agreement ? ', ' + agreement : '');

  // AMOUNT / HOURS (Bob's point 2). hours per date x number of dates = total hours; dollars only when the
  // rate is on the claim. A stated total that disagrees with the arithmetic blocks the claim.
  // Bob, 2026-09-29: "Add the total amount of moneys owed, it should already be in the claim itself." The claim's
  // own figure (amountClaimed, the builder's computeAmountClaimed) prints on its own line under the hours line.
  const owed = num(c.amountClaimed);
  if (!owed) missing.push('the total amount claimed (the claim holds no amount)');
  const hrs = num(c.hoursPerDate || c.hoursClaimed || c.totalHoursClaimed);
  const rateBasis = s(c.rateBasis) || 'punitive';
  const rate = num(c.hourlyRate);
  let amountLine;
  if (!hrs) {
    missing.push('the hours claimed for each date');
    // Record 607: one date reads "the date listed above", as the filled-in line does.
    amountLine = { hours: '', text: 'hours at the current ' + rateBasis + ' rate for ' + (dates.length > 1 ? 'each date listed above.' : 'the date listed above.') };
  } else {
    const total = hrs * dates.length;
    let t = 'hours at the current ' + rateBasis + ' rate for ' + (dates.length === 1 ? 'the date listed above.' :
      'each of the ' + dates.length + ' dates listed above, ' + fmtHours(total) + ' hours in all.');
    if (rate) {                                             // hourlyRate is the rate AT the basis named - never multiplied here
      const dollars = Math.round(total * rate * 100) / 100;
      if (owed && Math.abs(owed - dollars) > 0.01) missing.push('the total amount claimed (' + money(owed) + ') does not match ' + fmtHours(total) + ' hours at the rate on file (' + money(dollars) + ')');
    }
    amountLine = { hours: fmtHours(hrs), text: t };
  }
  amountLine.total = owed ? money(owed) : '';

  // SUBSTANCE OF CLAIM - the Statement of Claim (proposed ruling 258, above).
  const substance = [];
  const place = s(c.eventLocation || c.workLocation);
  if (!place) missing.push('the location');
  need('the claimant\u2019s own description of what happened', c.description);
  const lead = rules[0];
  rules.forEach((r) => {
    const cit = (Array.isArray(r.citations) ? r.citations : [])[0];
    if (!s(r.exactLanguage)) missing.push('the text of ' + s(r.rule));
    else if (!cit || cit.confirmed !== true) check.push('The text of ' + s(r.rule) + ' is not yet confirmed against the signed agreement. Compare it before signing.');
  });
  // Record 605 (ruling 138): a statement the claimant WITHDREW stays on the claim (factsWithdrawn marks it) but is not
  // an approval, and the platform drafts no replacement for it - the form waits for a statement she stands behind.
  const socWithdrawn = !!(c.factsWithdrawn && c.factsWithdrawn.statementOfClaim);
  const approved = !socWithdrawn && c.statementOfClaim && nonEmpty(c.statementOfClaim.text) && nonEmpty(c.statementOfClaim.approvedBy);
  let statement = '', drafted = false;
  if (approved) statement = s(c.statementOfClaim.text);
  // Template: by the rule's violation id; failing that, by the kind of claim the member chose (ruling 261).
  // SECTION 516 (2026-09-29): ONLY the kind of claim the member CHOSE picks a template. Picking it by the matched rule's
  // violation id alone drafted claim 0012 (a vacation claim wrongly matched to Rule 3(C), bids) as "the Carrier
  // awarded the position ... The Claimant was the senior qualified bidder" - facts the member never stated. A claim
  // with no chosen kind gets no draft; her own words stand.
  const tplId = lead && KIND_TEMPLATE[s(c.claimKind)];
  if (approved) { /* printed as approved */ }
  else if (!socWithdrawn && lead && tplId && TEMPLATES[tplId] && s(lead.exactLanguage) && (nonEmpty(c.violatorName) || nonEmpty((c.box1Answers || {}).q_junior))) {
    let q = s(lead.exactLanguage).replace(/\s+/g, ' ');
    q = lowerFirst(q.replace(/^\([A-Z]\)\s*/, ''));
    statement = TEMPLATES[tplId](c, lead, { date: longDate(occurred), place, seniority: longDate(seniority), quote: q.replace(/\.$/, '') + '.', answers: c.box1Answers || {} });
    drafted = true;
  }
  if (!approved && socWithdrawn) missing.push('a Statement of Claim the claimant stands behind (she withdrew hers on ' + s(c.factsWithdrawn.statementOfClaim.at).slice(0, 10) + ')');   // Record 605 (ruling 138)
  else if (!approved) missing.push('the claimant\u2019s approval of the Statement of Claim' + (drafted ? ' (drafted from the claim and ' + s(lead.rule) + ')' : ''));
  if (statement) substance.push({ text: statement });
  else if (nonEmpty(c.description)) substance.push({ text: s(c.description) });   // no template: her own words, and it cannot send until approved
  // Record 607 (Bob's test draft 0fE2NY93, 10/8): two paragraphs of one rule printed the heading "RULE NO. 5 ... provides:"
  // twice. A rule's heading now prints once; its further paragraphs follow under it, unlabelled.
  let lastRuleLabel = '';
  rules.slice(statement && drafted ? 1 : 0).forEach((r) => {
    const q = s(r.exactLanguage); if (!q) return;
    const lab = ruleName(r) + ' provides:';
    substance.push(lab === lastRuleLabel ? { label: '', text: '', quote: q, continues: true } : { label: lab, text: '', quote: q });
    lastRuleLabel = lab;
  });
  if (nonEmpty(c.witnesses)) substance.push({ label: 'Witnesses:', text: s(c.witnesses) });
  // Record 607 (Bob's draft 0fE2NY93): the narrative named "Todd" with no title and no junior man; Box 1 asks both.
  // Information for the signer, never a block (ruling 27: the platform adds no facts).
  const b1 = c.box1Answers || {};
  if (!nonEmpty(b1.q_manager) && !nonEmpty(c.violatorName)) check.push('Box 1 does not name who made the decision (name and title). Add it, or name him in the Statement of Claim, before signing.');
  if (!nonEmpty(b1.q_junior) && !nonEmpty(c.personsInvolved)) check.push('Box 1 does not name the other employee involved (name and roster number). Add it before signing if one was involved.');
  // Bob 2026-10-17 (plan YCP_PLAN_OVERTIME_CLAIM_FACTS_2026-10-17.md, A): an overtime claim that does not name the job is
  // a claim the Carrier can deny for want of facts. Asked only where the overtime questions were shown (the answer key
  // is one whose category asks the job question). Information for the signer, never a block.
  if (nonEmpty(c.claimKind) && guidedQuestions(c.claimKind).some(q => q.id === 'q_ot_job') && !nonEmpty(b1.q_ot_job)) {
    check.push('Box 1 does not name the job (job symbol, tour or starting time, yard or location). Add it before signing.');
  }

  // ATTACHMENTS - what the claimant attaches, named for the carrier.
  const attachments = (Array.isArray(c.exhibits) ? c.exhibits : []).map(e => attachmentLabel(e && (e.description || e.name), claimant)).filter(nonEmpty);
  attachments.push(AWARDS_ATTACHMENT);   // section 537: the awards the closing and payment paragraphs rely on
  check.push('Attach copies of the awards listed (links are on the claim page).');
  const uploaded = Array.isArray(c.documents) ? c.documents.length : 0;
  if (attachments.length && !uploaded) check.push('No attachment is uploaded to this claim yet. Attach a copy of each item listed before serving the claim.');

  // WHO SIGNS (ruling 260, Bob 2026-09-29): "whoever makes the submission to the carrier must sign the claim."
  // The claimant making her own claim signs - her signature is her authority. If she elects not to sign, the
  // Local or General Chairperson may still submit it with his own signature as a union officer; she then gets a
  // warning, and her acknowledgment is recorded (claimantDeclinedSignature, written by the claim screen).
  //   opts.filing = { by: 'claimant' | 'local_chair' | 'general_chair', claimantSigns: true|false,
  //                   officer: { name, title, address: [line, line] } }
  const fl = o.filing || { by: 'claimant', claimantSigns: true };
  const claimantAddr = [s(c.claimantStreet), [s(c.claimantCity), [s(c.claimantState), s(c.claimantZip)].filter(nonEmpty).join(' ')].filter(nonEmpty).join(', ')].filter(nonEmpty);
  const signers = [];
  const claimantSigns = fl.by === 'claimant' || fl.claimantSigns !== false;
  if (claimantSigns) {
    if (claimantAddr.length < 2) missing.push('the claimant\u2019s mailing address');
    signers.push({ nameLine: claimant + ', Claimant', address: claimantAddr.length >= 2 ? claimantAddr : [], submits: fl.by === 'claimant', field: 'claimant' });
  } else {
    if (!(c.claimantDeclinedSignature && nonEmpty(c.claimantDeclinedSignature.acknowledgedAt))) check.push('The claimant has not signed. Her acknowledgment of the unsigned-claim warning is not yet recorded; she will see it the next time she opens the claim.');
  }
  if (fl.by !== 'claimant') {
    const off = fl.officer || {};
    const title = s(off.title) || (fl.by === 'general_chair' ? 'General Chairperson' : 'Local Chairperson');
    if (!nonEmpty(off.name)) missing.push('the name of the ' + title + ' submitting the claim');
    const addr = (off.address || []).filter(nonEmpty);
    if (addr.length < 2) missing.push('the mailing address of the ' + title + ' submitting the claim');
    signers.push({ nameLine: (s(off.name) || '') + (nonEmpty(off.name) ? ', ' : '') + title, address: addr.length >= 2 ? addr : [], contact: s(off.contact), submits: true, field: fl.by === 'general_chair' ? 'gc' : 'lc' });
  }

  // RULING 262: the officer the claim is presented to. The agreement names the TITLE (opts.presentment, from
  // claimRequirements.js); the filer names the person. Blank is never guessed - it is listed as missing.
  const pres = o.presentment || {};
  const presentedTo = s(c.presentedTo);
  if (!presentedTo) missing.push('who the claim is presented to (name and title' + (s(pres.officer) ? ' - the agreement designates ' + s(pres.officer) : '') + ')');
  // Record 610 (ruling 262): the three-way check - src\data\presentedToCheck.js crossCheck(), passed in by the caller.
  // Two directories that differ and no recorded choice: on the not-ready list - never a block on saving or filing.
  // A title the agreement does not name, or an answer the directories do not bear out: a check before signing.
  // Without a check passed in, nothing here changes (scripts\test-presented-to-check.mjs proves it).
  if (pres.check && nonEmpty(pres.check.formMissing)) missing.push(s(pres.check.formMissing));
  if (pres.check && Array.isArray(pres.check.formChecks)) pres.check.formChecks.forEach(t => { if (nonEmpty(t)) check.push(s(t)); });

  const form = {
    header: (o.header || []).filter(nonEmpty),
    contact: s(o.contact),                                   // letterhead contact line - from the record only
    craft: s(c.craft),                                        // centered under the letterhead (Bob, 2026-09-29)
    title: 'Penalty Time Claim',
    number,
    claimant,
    rows: [
      [['Claim of', claimant], ['Employee No.', employeeNo]],
      [['Seniority Date', longDate(seniority)], ['Submission Date', c.presentedOn ? longDate(c.presentedOn) : '', 'submissionDate']],   // section 528: the service date once recorded; blank until then, filled in at service
      [['Date of Violation', longDate(occurred)], ['Time of Violation', time]],
    ],
    // One date: the Date of Violation row says it all. More than one: the rest, ranges collapsed.
    // RULING 262 (2026-09-29): who the claim is presented to. Tonight, the filer's own answer (presentedTo) with the
    // agreement's designation printed under it; the GC and LC directories and their cross-check follow.
    full: [['Presented to', presentedTo]]
      .concat(dates.length > 1 ? [['Additional Claim Dates', dateRanges(dates.filter(d => d !== occurred))]] : [])
      .concat([['Article(s) Violated', articles]]),
    presentNote: s(pres.officer) ? 'The officer the agreement designates: ' + s(pres.officer) + '.' : '',
    amount: amountLine,
    standing: STANDING_SENTENCE,
    substance,
    closing: CLOSING_PARAGRAPH + ' ' + CLOSING_AWARDS,
    attachments,
    payment: PAYMENT_PARAGRAPH,
    signers,
    // Section 537: the receipt line per the property's service terms; null prints none (service recorded elsewhere).
    receipt: o.service && Object.prototype.hasOwnProperty.call(o.service, 'receipt') ? o.service.receipt : DEFAULT_RECEIPT,
    serviceNote: s(o.service && o.service.note),
    footer: 'Recorded on Labor On The Record',
  };
  const uniq = Array.from(new Set(missing));
  return { form, missing: uniq, check, sendable: uniq.length === 0 };
}

// THE FORM AS THE CLAIM PAGE BUILDS IT - one builder, two callers (ruling 271, 2026-10-02). This is the body of
// ClaimFormPanel's model, moved here unchanged so the copy the filer is given at the moment of filing is built by the
// same code that draws the form on the claim page - not a second form that could drift (PD-10 / SS XI). Pure: what the
// page loads (GC name, the signed-in person's profile, the property's service setting, the agreement's requirements)
// and the rule-text resolver are passed in.
//   ctx: { committee, gcName, me, by, claimantSigns, user, requirements, service, resolveRule }
export function formRoleOf(user) {
  if (!user) return 'claimant';
  if (user.role === 'local_chair') return 'local_chair';
  if (user.role === 'general_chair') return 'general_chair';
  return 'claimant';   // the claimant, and the administrator viewing as the claimant would
}
// Claims finish item 2 (2026-10-12), with ruling 305.8: the form prints the signed-in person's own name and address under
// the office chosen, so an officer may prepare it only as the office he holds - or as the claimant's copy. A Local
// Chairperson could choose "General Chairperson" and print his own name under her office. The administrator keeps all
// three for building and testing.
export const FORM_ROLES = Object.freeze([['claimant', 'The claimant'], ['local_chair', 'Local Chairperson'], ['general_chair', 'General Chairperson']]);
export function formRolesFor(user) {
  if (!user) return ['claimant'];
  if (user.isAdmin) return FORM_ROLES.map(([k]) => k);
  if (user.role === 'local_chair') return ['claimant', 'local_chair'];
  if (user.role === 'general_chair') return ['claimant', 'general_chair'];
  return ['claimant'];
}
export function claimFormModel(claim, ctx) {
  if (!claim) return null;
  const x = ctx || {};
  const committee = x.committee || '';
  const by = x.by || 'claimant';
  const me = x.me || null;
  const user = x.user || null;
  // Rule text from the catalogue, verbatim, where the claim does not already carry it.
  const rules = (Array.isArray(claim.matchedRules) ? claim.matchedRules : []).map(r => {
    if (!r || s(r.exactLanguage) || !r.rule || typeof x.resolveRule !== 'function') return r;
    const t = x.resolveRule(claim.applicableAgreement, r.rule, claim.eventLocation || claim.workLocation, claim.incidentDate);
    return t && s(t.text) ? { ...r, exactLanguage: s(t.text) } : r;
  });
  const header = [(x.gcName ? String(x.gcName).toUpperCase() + ', ' : '') + 'General Chairperson, ' + (committee || '') + ', SMART-TD'];
  // Record 610 (ruling 259): an officer's signature carries the office address his seat holds - the GC's letterhead
  // address lines, the LC's seat directory (src\data\contactDetails.js officeAddressFor, passed in as officeAddress);
  // without one, his own profile address, exactly as before.
  const ownAddr = me ? [s(me.street), [s(me.city), [s(me.state), s(me.zip)].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean) : [];
  const office = Array.isArray(x.officeAddress) ? x.officeAddress.map(s).filter(Boolean) : [];
  const addr = by !== 'claimant' && office.length >= 2 ? office : ownAddr;
  const title = by === 'general_chair' ? 'General Chairperson, ' + (committee || '')
    : 'Local Chairperson' + (s(claim.scopeKey) ? ', ' + s(claim.scopeKey) : '');
  // Bob 2026-10-12: "if the claimant is filing and representing himself, its signature before submitting, if its the lc
  // making the submission for the claimant, the lc still signs it, and the same for the gc". Once service on the carrier is
  // recorded, the block that signed it is fixed on the claim (presentmentSigner) and printed from there - never again from
  // whoever is viewing.
  const frozen = claim.presentmentSigner && typeof claim.presentmentSigner === 'object' ? claim.presentmentSigner : null;
  const filing = frozen ? (frozen.by === 'claimant' ? { by: 'claimant', claimantSigns: true }
      : { by: s(frozen.by), claimantSigns: frozen.claimantSigns !== false, officer: { name: s(frozen.name), title: s(frozen.title), address: Array.isArray(frozen.address) ? frozen.address.map(s).filter(Boolean) : [], contact: s(frozen.contact) } })
    : by === 'claimant' ? { by: 'claimant', claimantSigns: true }
    : { by, claimantSigns: x.claimantSigns !== false, officer: { name: s(me && me.name) || s(user && user.name), title, address: addr, contact: [s(me && me.phone), s(user && user.email)].filter(Boolean).join('  \u00b7  ') } };   // Bob 2026-10-12 "3. a": his own corrected profile name first
  const req = x.requirements || null;
  // Section 521: a draft has no claim number yet (it is taken at filing); the form says so rather than print the record id.
  const model = buildPenaltyClaim({ ...claim, id: claim.status === 'draft' ? 'DRAFT (numbered when filed)' : claim.id, matchedRules: rules }, { header, filing, presentment: { officer: req && req.presentmentOfficer, rule: req && req.claimsRule, check: x.presentedToCheck || undefined }, service: x.service || undefined });   // sections 537/539: the property's receipt line
  // Record 662 (Bob 2026-10-12, "yes"): an example claim (a tester's, or one the administrator marked) prints as one - the
  // PDF carries EXAMPLE ONLY on every page, so a printed copy can never pass for a real claim.
  if (model && claim.isExample === true) model.example = true;
  // The block this form is signed with - what presentmentPatch fixes on the claim when service is recorded.
  if (model) {
    model.signer = filing.by === 'claimant' ? { by: 'claimant', name: s(claim.claimantName) }
      : { by: filing.by, claimantSigns: filing.claimantSigns !== false, name: s(filing.officer.name), title: s(filing.officer.title),
          address: (filing.officer.address || []).map(s).filter(Boolean), contact: s(filing.officer.contact) };
    model.signerFrozen = !!frozen;
  }
  return model;
}
// The file name the claim page uses for the form; the filer's copy at filing carries FILED_COPY (ruling 271).
export function claimFormFileName(claim, model, suffix) {
  // Record 651 (ruling 305.11; 305.2, 305.5): the version tag - v1, v2 ... or FV once served - is the name's LAST part.
  return withVersionTag('Penalty_Time_Claim_' + s((claim && (claim.id || claim.title)) || 'draft').replace(/[^A-Za-z0-9_-]+/g, '_') + (suffix ? '_' + suffix : '') + (model && model.sendable ? '' : '_PREVIEW') + '.pdf', claim);
}

// The Statement of Claim as the platform would draft it (ruling 258), for the screen that shows it beside
// the member's own words (ruling 261). Never the approved text: an approved statement is on the claim.
// Returns { text, drafted, rule } - drafted false means no template fits and her own words stand.
export function draftStatementOfClaim(claim) {
  const c = Object.assign({}, claim || {}, { statementOfClaim: null });
  const m = buildPenaltyClaim(c, {});
  const first = m.form.substance[0];
  const drafted = m.missing.some(x => /drafted from the claim/.test(x));
  const lead = (Array.isArray(c.matchedRules) ? c.matchedRules : []).filter(r => r && !r.setAsideAt)[0];
  return { text: drafted && first ? first.text : '', drafted, rule: lead ? String(lead.rule || '') : '' };
}
